import {
  BanknoteIcon,
  CalendarDaysIcon,
  TagIcon,
  TargetIcon,
  WalletIcon,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import {
  BudgetLineFragment,
  BudgetLineMonthQuery,
  RecordBudgetLinesQuery,
} from "@/__generated__/graphql";
import { useAppRouteNavigation } from "@/app/app/_/routing";
import { useBudgetViewStore } from "@/app/app/_/views/BudgetView/BudgetView.store";
import {
  BudgetViewBar,
  nameBudgetLine,
} from "@/app/app/_/views/BudgetView/BudgetViewMonth";
import { BudgetViewPlanAmount } from "@/app/app/_/views/BudgetView/BudgetViewPlan";
import { SelectableCardList } from "@/components/molecules/SelectableCardList";
import { SelectableListItemRow } from "@/components/molecules/SelectableListItemRow";
import { useNodeLayoutStore } from "@/components/organisms/Node/Node.hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { Mutations, Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useEnumColor } from "@/hooks/useEnumColor";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { ObjectType } from "@/objects/@types";
import { State } from "@/state";
import { Utils } from "@/utils";

const BUCKET_OPTIONS = [
  { key: "Need", value: "NEED" },
  { key: "Want", value: "WANT" },
];

const KIND_OPTIONS = [
  { key: "A bill", value: "BILL" },
  { key: "Everyday", value: "EVERYDAY" },
  { key: "Saving for", value: "SAVING" },
  { key: "Paying off", value: "DEBT" },
];

type HistoryEntry = {
  key: string;
  on: string;
  description: string | null;
  amount: string;
  /** What the payment is in - said only where it is not its business's own. */
  currency?: string;
};

const HISTORY_PLACEHOLDER: HistoryEntry = {
  key: "placeholder",
  on: "2026-11-03",
  description: "No Frills",
  amount: "120.00",
};

type BudgetLineMonthFigures = NonNullable<
  Extract<
    NonNullable<BudgetLineMonthQuery["node"]>,
    { __typename?: "BudgetLineObject" }
  >["month"]
>;

/** A typical line's month, drawn in skeletons while its month is read. */
const FIGURES_PLACEHOLDER: BudgetLineMonthFigures = {
  __typename: "BudgetLineMonthObject",
  amountPlanned: "400.00",
  amountSpent: "120.00",
  amountDue: "0.00",
  amountLeft: "280.00",
  amountSaved: "280.00",
  amountOwed: "280.00",
  amountNeeded: "0.00",
  bills: [],
};

/** The line the panel is open on, and its month - the one the Budget view shows. */
function useLineMonth() {
  const store = useNodeLayoutStore();
  const line = store(
    useShallow((state) => state.fieldValues as BudgetLineFragment | undefined),
  );
  const month = useBudgetViewStore((state) => state.month);
  const { data, loading } = useBackendQuery(Queries.BUDGET_LINE_MONTH_QUERY, {
    variables: { id: line?.id ?? "", month },
    skip: !line?.id,
    fetchPolicy: "cache-and-network",
  });
  const node = data?.node?.__typename === "BudgetLineObject" ? data.node : null;
  const currency =
    line?.amountCurrency !== line?.entity?.currency
      ? (line?.amountCurrency ?? undefined)
      : undefined;
  // Read till the panel has its line and the line its month.
  return {
    line,
    month,
    node,
    currency,
    isLoading: !line || (loading && !node),
  };
}

/**
 * The line this month: for spending, what is left of what was planned; for a
 * bill, what was paid and what is due; for a goal, what is saved towards it;
 * for a debt, what is still owed - and what it gets each month, typed.
 */
function BudgetLineOverviewTab() {
  const { line, month, node, currency, isLoading } = useLineMonth();
  const red = useEnumColor(EnumColors.RED);
  const orange = useEnumColor(EnumColors.ORANGE);
  // Its month as read - a typical one, in skeletons, while it is read.
  const figures = isLoading ? FIGURES_PLACEHOLDER : node?.month;
  if (!line || !figures) {
    return (
      <Typography
        level="body-sm"
        data-testid="budget-line-overview-none"
        className="text-muted-foreground/80 p-4"
      >
        Not in this month's budget
      </Typography>
    );
  }
  const money = (amount: string | number) =>
    Utils.fCurrency(amount, false, true, currency);
  const kind = line.kind;
  const planned = Number(figures.amountPlanned);
  const left = Number(figures.amountLeft);
  const isOver = left < 0 && kind !== "SAVING" && kind !== "DEBT";
  const monthName = new Date(`${month}T12:00:00`).toLocaleDateString(
    undefined,
    { month: "long" },
  );

  // What a goal has saved and a debt still owes, as the month says them: nothing
  // in their place where it says none.
  const saved = figures.amountSaved;
  const owed = figures.amountOwed;
  const headline =
    kind === "SAVING"
      ? {
          amount: saved,
          words: line.amountGoal
            ? `saved of ${money(line.amountGoal)}`
            : "saved",
          progress:
            line.amountGoal && Number(line.amountGoal) > 0 && saved != null
              ? Number(saved) / Number(line.amountGoal)
              : 0,
        }
      : kind === "DEBT"
        ? {
            amount: owed,
            words: `still owed, of ${money(line.amountOpening)} when the budget started`,
            progress:
              Number(line.amountOpening) > 0 && owed != null
                ? 1 - Number(owed) / Number(line.amountOpening)
                : 0,
          }
        : {
            amount: Math.abs(left),
            words: left < 0 ? `over in ${monthName}` : `left in ${monthName}`,
            progress:
              planned > 0
                ? (Number(figures.amountSpent) + Number(figures.amountDue)) /
                  planned
                : 0,
          };
  const facts: [string, string][] =
    kind === "SAVING"
      ? [
          ["Put by each month", figures.amountPlanned],
          ["Spent from it this month", figures.amountSpent],
          ...(Number(figures.amountNeeded) > 0
            ? [
                ["Needed a month for the goal", figures.amountNeeded] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]
      : kind === "DEBT"
        ? [["Paid off each month", figures.amountPlanned]]
        : [
            ["Planned", figures.amountPlanned],
            [kind === "BILL" ? "Paid" : "Spent", figures.amountSpent],
            ...(Number(figures.amountDue) > 0
              ? [["Billed and due", figures.amountDue] as [string, string]]
              : []),
          ];

  return (
    <div data-testid="budget-line-overview" className="flex flex-col gap-3 p-4">
      <div>
        <Typography
          level="h2"
          data-testid="budget-line-headline"
          className="tabular-nums"
          style={isOver ? { color: red.color } : undefined}
        >
          <Skeleton loading={isLoading} variant="inline">
            {headline.amount != null ? money(headline.amount) : null}
          </Skeleton>
        </Typography>
        <Typography level="body-sm" className="text-muted-foreground">
          {headline.words}
        </Typography>
      </div>
      <BudgetViewBar
        height={8}
        value={headline.progress}
        enumColor={
          kind === "SAVING"
            ? EnumColors.GREEN
            : kind === "DEBT"
              ? EnumColors.AQUA
              : isOver
                ? EnumColors.RED
                : EnumColors.BLUE
        }
      />
      <div className="flex flex-col gap-1">
        {facts.map(([label, amount]) => (
          <div key={label} className="flex justify-between">
            <Typography level="body-sm">{label}</Typography>
            <Typography level="body-sm" className="tabular-nums">
              <Skeleton loading={isLoading} variant="inline">
                {money(amount)}
              </Skeleton>
            </Typography>
          </div>
        ))}
        {figures.bills.map((bill) => (
          <Typography
            key={bill.id}
            level="body-sm"
            data-testid="budget-line-bill"
            style={{ color: orange.color }}
          >
            {/* What it says is due and when, where it says them: no amount or day made up. */}
            {[
              bill.amountDue != null
                ? `${bill.name}: ${money(bill.amountDue)} due`
                : `${bill.name} due`,
              bill.dateDue
                ? new Date(bill.dateDue).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })
                : null,
            ]
              .filter(Boolean)
              .join(" ")}
          </Typography>
        ))}
      </div>
      {!isLoading && (
        <div className="flex items-center gap-2">
          <Typography level="body-sm" className="flex-1">
            Each month
          </Typography>
          <BudgetViewPlanAmount
            amount={line.amountPlanned}
            currency={currency}
            label="Each month"
            testId="budget-line-planned"
            mutation={Mutations.PLAN_BUDGET_LINE}
            input={(amount) => ({
              lineIdentifier: line.identifier,
              amountPlanned: amount,
            })}
          />
        </div>
      )}
    </div>
  );
}

/** What was spent on the line this month, newest first: each payment on its accounts - a refund below nothing. */
function BudgetLinePaymentsTab() {
  const { line, node, isLoading } = useLineMonth();
  const entries: HistoryEntry[] = (node?.history ?? []).map((entry, index) => ({
    key: entry.transaction?.id ?? String(index),
    on: entry.on,
    description: entry.description ?? null,
    amount: entry.amount,
    currency:
      entry.amountCurrency !== line?.entity?.currency
        ? entry.amountCurrency
        : undefined,
  }));
  return (
    <SelectableCardList<HistoryEntry>
      size="md"
      layout="flush"
      items={entries}
      loading={isLoading}
      placeholderItem={HISTORY_PLACEHOLDER}
      getItemKey={(entry) => entry.key}
      emptyState={{ title: "Nothing spent on it this month" }}
      renderItem={({ item: entry, isLoading: isRowLoading }) => (
        <SelectableListItemRow
          title={entry.description ?? "No description"}
          description={new Date(`${entry.on}T12:00:00`).toLocaleDateString(
            undefined,
            { month: "short", day: "numeric" },
          )}
          isLoading={isRowLoading}
          rootDataAttrs={{ "data-testid": "budget-line-payment" }}
          titleEnd={`${Number(entry.amount) < 0 ? "+" : ""}${Utils.fCurrency(Math.abs(Number(entry.amount)), false, true, entry.currency)}`}
        />
      )}
    />
  );
}

type RecordBudgetLine = Extract<
  NonNullable<RecordBudgetLinesQuery["node"]>,
  { budgetLines: unknown }
>["budgetLines"][number];

/** A typical line a record lands on: what is drawn in a skeleton while its lines are read. */
const PLACEHOLDER_RECORD_LINE = {
  __typename: "BudgetLineAfterObject",
  amountLeft: "280.00",
  line: {
    __typename: "BudgetLineObject",
    id: btoa("BudgetLineObject:placeholder"),
    identifier: "placeholder",
    name: "",
    kind: "EVERYDAY",
    amountCurrency: "CAD",
    entity: { __typename: "EntityObject", id: "", currency: "CAD" },
    chartedAccount: {
      __typename: "ChartedAccountObject",
      id: "",
      name: "Groceries",
    },
  },
} as RecordBudgetLine;

/**
 * In a payment's or a bill's panel: the lines of the budget its booking or its
 * lines land on, with what each has left this month - each opening in the
 * Budget view.
 */
export function BudgetLinesOfRecord({ id }: { id: string | undefined }) {
  const navigation = useAppRouteNavigation();
  const { data, loading } = useBackendQuery(Queries.RECORD_BUDGET_LINES_QUERY, {
    variables: { id: id ?? "" },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });
  const node =
    data?.node?.__typename === "TransactionObject" ||
    data?.node?.__typename === "DocumentObject"
      ? data.node
      : null;
  const isLoading = loading && !node;
  // Nothing where there is nothing to read; while it is read, a typical line in its skeleton.
  if (!node && !isLoading) return null;
  const lines = node?.budgetLines ?? [PLACEHOLDER_RECORD_LINE];
  if (lines.length === 0) {
    return (
      <Typography level="body-sm" className="text-muted-foreground/80">
        Not in the budget's plan
      </Typography>
    );
  }
  return (
    <div data-testid="record-budget-lines" className="flex flex-wrap gap-1">
      {lines.map((row) => {
        const left = Number(row.amountLeft);
        const amount = Utils.fCurrency(
          Math.abs(left),
          false,
          true,
          row.line.amountCurrency !== row.line.entity?.currency
            ? (row.line.amountCurrency ?? undefined)
            : undefined,
        );
        return (
          <Typography
            key={row.line.id}
            level="body-sm"
            component="button"
            disabled={isLoading}
            onClick={() => {
              navigation.navigateToView("budget");
              State.getState().openNodePanel(row.line.id);
            }}
            className="p-0 cursor-pointer text-left"
          >
            {/* Its name, where it has one to say, and what it has left. */}
            <Skeleton loading={isLoading} variant="inline">
              {[
                nameBudgetLine(row.line),
                row.line.kind === "SAVING"
                  ? `${amount} saved`
                  : row.line.kind === "DEBT"
                    ? `${amount} owed`
                    : left < 0
                      ? `${amount} over`
                      : `${amount} left`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Skeleton>
          </Typography>
        );
      })}
    </div>
  );
}

const object: ObjectType<BudgetLineFragment> = {
  humanName: "budget line",
  inputKey: "budgetLine",
  typename: "BudgetLineObject",
  ReactIcon: WalletIcon,
  getTitle: (data) => (data ? nameBudgetLine(data) : ""),
  getEntityId: (data) => data?.entity?.id ?? null,
  groups: [
    {
      id: "overview",
      label: "Overview",
      isVisible: true,
      component: BudgetLineOverviewTab,
      flush: true,
    },
    {
      id: "payments",
      label: "Payments",
      isVisible: true,
      component: BudgetLinePaymentsTab,
      flush: true,
    },
    { id: "settings", label: "Settings", isVisible: true },
  ],
  fieldDefinitions: [
    {
      objectKey: "name",
      label: "Name",
      type: "string",
      ReactIcon: TagIcon,
      groupId: "settings",
      asString: (data) => data?.name ?? "",
      getValue: (data) => data?.name,
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => !data?.chartedAccount,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "kind",
      label: "Part of the month",
      type: "enum",
      ReactIcon: WalletIcon,
      groupId: "settings",
      asString: (data) => data?.kind ?? "",
      getValue: (data) => data?.kind,
      getValueOptions: () => ({ enum: KIND_OPTIONS }),
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => Boolean(data?.chartedAccount),
      },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "bucket",
      label: "Need or want",
      type: "enum",
      ReactIcon: WalletIcon,
      groupId: "settings",
      asString: (data) => data?.bucket ?? "",
      getValue: (data) => data?.bucket,
      getValueOptions: () => ({
        enum: BUCKET_OPTIONS,
        getColors: (mode: string | undefined) => {
          const resolved = mode === "dark" ? "dark" : "light";
          return {
            NEED: EnumColors.BLUE[resolved],
            WANT: EnumColors.PURPLE[resolved],
          };
        },
      }),
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => data?.kind === "BILL" || data?.kind === "EVERYDAY",
      },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "isSpread",
      label: "Spread over months",
      type: "boolean",
      ReactIcon: CalendarDaysIcon,
      groupId: "settings",
      asString: (data) => (data?.isSpread ? "Yes" : "No"),
      getValue: (data) => data?.isSpread,
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => data?.kind === "BILL" || data?.kind === "EVERYDAY",
      },
      fieldProps: {
        headerFieldType: "boolean",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "amountPlanned",
      label: "Each month",
      type: "currency",
      ReactIcon: BanknoteIcon,
      groupId: "settings",
      asString: (data) => data?.amountPlanned ?? "",
      getValue: (data) => data?.amountPlanned,
      updateProps: { isRequired: false, isEditable: true },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "amountGoal",
      label: "Goal",
      type: "currency",
      ReactIcon: TargetIcon,
      groupId: "settings",
      asString: (data) => data?.amountGoal ?? "",
      getValue: (data) => data?.amountGoal,
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => data?.kind === "SAVING",
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "goalBy",
      label: "By",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      groupId: "settings",
      asString: (data) => data?.goalBy ?? "",
      getValue: (data) => data?.goalBy,
      updateProps: {
        isRequired: false,
        isEditable: true,
        isVisible: (data) => data?.kind === "SAVING",
      },
      fieldProps: {
        headerFieldType: "date",
        isSortable: false,
        isFilterable: false,
      },
    },
  ],
};

export { object as BudgetLineObject };
